import type { Address } from "viem"
import { action, computed, makeObservable, observable } from "mobx"

import { LcAddress } from "@/domains/core"
import { IEventBus } from "@/domains/events"

import { WalletConnectedEvent, WalletDisconnectedEvent } from "./events"

export class WalletConnectionStore {
  // Fixed configuration - not observable
  private readonly expectedChainId: number
  private readonly safeAddress: Address

  // Observable state
  connectedAddress: LcAddress | null = null
  isConnected = false
  currentChainId: number | null = null
  connectorId: string | null = null
  connectorName: string | null = null
  connectorType: string | null = null

  constructor(
    expectedChainId: number,
    safeAddress: Address,
    private readonly eventBus: IEventBus,
  ) {
    this.expectedChainId = expectedChainId
    this.safeAddress = safeAddress

    makeObservable(this, {
      connectedAddress: observable,
      currentChainId: observable,
      isConnected: observable,
      connectorId: observable,
      connectorName: observable,
      connectorType: observable,
      isOnCorrectChain: computed,
      isWalletConnected: computed,
      connectWallet: action,
      disconnectWallet: action,
      updateChainId: action,
    })
  }

  // Computed getters
  get isWalletConnected(): boolean {
    return this.isConnected && this.connectedAddress !== null
  }

  get isOnCorrectChain(): boolean {
    return this.currentChainId === this.expectedChainId
  }

  get expectedChainIdValue(): number {
    return this.expectedChainId
  }

  // Actions
  connectWallet(address: string, connectorId?: string | null, connectorName?: string | null, connectorType?: string | null): void {
    if (!address || typeof address !== "string") {
      console.warn("connectWallet called with invalid address:", address)
      return
    }
    this.connectedAddress = new LcAddress(address)
    this.isConnected = true
    this.connectorId = connectorId ?? null
    this.connectorName = connectorName ?? null
    this.connectorType = connectorType ?? null
    // Publish wallet connected event
    this.eventBus.publish(new WalletConnectedEvent(address))
  }

  disconnectWallet(): void {
    const oldAddress = this.connectedAddress?.toString() ?? ""
    this.connectedAddress = null
    this.isConnected = false
    this.connectorId = null
    this.connectorName = null
    this.connectorType = null
    // Publish wallet disconnected event
    this.eventBus.publish(new WalletDisconnectedEvent(oldAddress))
  }

  updateChainId(chainId: number): void {
    const oldChainId = this.currentChainId
    this.currentChainId = chainId
    // console.log(
    //   `🔄 WalletConnectionStore: Chain changed from ${oldChainId} to ${chainId}, expected: ${this.expectedChainId}`,
    // )
    // console.log(`🔍 WalletConnectionStore: isOnCorrectChain = ${this.isOnCorrectChain}`)
  }

  // Utility methods
  getConnectedAddress(): Address | null {
    return this.connectedAddress?.raw ?? null
  }

  get safeLensAddress(): Address {
    if (this.connectedAddress?.raw) {
      return this.connectedAddress.raw
    }
    // Ensure safeAddress is valid
    if (!this.safeAddress || typeof this.safeAddress !== "string") {
      throw new Error("safeAddress is not set or invalid in WalletConnectionStore")
    }
    return this.safeAddress
  }

  isConnectedToAddress(address: string): boolean {
    return this.isConnected && (this.connectedAddress?.equalsString(address) ?? false)
  }
}
